import { Platform, Alert } from 'react-native';
import Papa from 'papaparse';
import { Buffer } from 'buffer';
import {
	SHOW_LOADING_MODAL,
	HIDE_LOADING_MODAL,
	SHOW_AUTH_MODAL,
	CHANGE_DATA,
	CHANGE_DATA_TAGS,
	SET_UPLOADER_STAGE,
} from '@reduxLocal/actions/types';
import { uploadGlobalAsset, analyzeImage, checkImageAnalysisHealth } from '@api/api';
import { navigationRef } from '@navigation/rootNavigation';
import { COLORS } from '@navigation/screens/MidLevel/MessageScreen/Components/Misc';

// Helper function to detect file type
export const getFileType = (
	fileName: string,
	mimeType?: string
): 'csv' | 'excel' | 'image' | 'video' | 'document' | 'other' => {
	const lowerName = fileName.toLowerCase();

	if (lowerName.endsWith('.csv') || mimeType === 'text/csv') return 'csv';
	if (
		lowerName.endsWith('.xlsx') ||
		lowerName.endsWith('.xls') ||
		mimeType ===
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
		mimeType === 'application/vnd.ms-excel'
	)
		return 'excel';

	if (
		mimeType?.startsWith('image/') ||
		['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'].some((ext) =>
			lowerName.endsWith(ext)
		)
	) {
		return 'image';
	}

	if (
		mimeType?.startsWith('video/') ||
		['.mp4', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.webm'].some((ext) =>
			lowerName.endsWith(ext)
		)
	) {
		return 'video';
	}

	// Document types (PDF, Word, PowerPoint, etc.)
	if (
		mimeType === 'application/pdf' ||
		mimeType === 'application/msword' ||
		mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
		mimeType === 'application/vnd.ms-powerpoint' ||
		mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
		mimeType === 'application/rtf' ||
		mimeType === 'text/plain' ||
		mimeType === 'text/rtf' ||
		['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.rtf', '.txt', '.odt', '.ods', '.odp'].some((ext) =>
			lowerName.endsWith(ext)
		)
	) {
		return 'document';
	}

	return 'other';
};

// Helper function to detect CSV delimiter
export const detectDelimiter = (csvText: string): string => {
	const delimiters = [',', ';', '\t', '|'];
	const firstLine = csvText.split('\n')[0];
	let maxCount = 0;
	let bestDelimiter = ',';

	delimiters.forEach((delimiter) => {
		const count = (firstLine.match(new RegExp(`\\${delimiter}`, 'g')) || [])
			.length;
		if (count > maxCount) {
			maxCount = count;
			bestDelimiter = delimiter;
		}
	});

	return bestDelimiter;
};

// Helper function to detect data type from sample values
export const detectDataType = (values: any[]): string => {
	if (!values || values.length === 0) return 'string';

	const nonEmptyValues = values.filter(
		(v) => v !== null && v !== undefined && v !== ''
	);
	if (nonEmptyValues.length === 0) return 'string';

	const numberCount = nonEmptyValues.filter(
		(v) => !isNaN(Number(v)) && !isNaN(parseFloat(v))
	).length;
	if (numberCount === nonEmptyValues.length) {
		const integerCount = nonEmptyValues.filter((v) =>
			Number.isInteger(Number(v))
		).length;
		return integerCount === nonEmptyValues.length ? 'integer' : 'number';
	}

	const dateCount = nonEmptyValues.filter(
		(v) => !isNaN(Date.parse(v))
	).length;
	if (dateCount > nonEmptyValues.length * 0.8) {
		return 'date';
	}

	const boolCount = nonEmptyValues.filter(
		(v) =>
			typeof v === 'boolean' ||
			['true', 'false', 'yes', 'no', '1', '0'].includes(
				String(v).toLowerCase()
			)
	).length;
	if (boolCount === nonEmptyValues.length) {
		return 'boolean';
	}

	return 'string';
};

// Helper function to detect multiple tables in CSV text
export const detectMultipleTables = (csvText: string): boolean => {
	const lines = csvText.trim().split('\n');
	if (lines.length < 2) return false;

	// Count occurrences of "Table" in first column (case-insensitive)
	const tableRegex = /^Table/gi;
	let tableHeaderCount = 0;
	let emptyRowCount = 0;
	let headerRowCount = 0;
	let dataRegionCount = 0;

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i].trim();
		const firstCell = line.split(',')[0]?.trim() || '';

		// Check for "Table" headers
		if (tableRegex.test(firstCell)) {
			tableHeaderCount++;
		}

		// Check for empty rows
		if (line === '' || line.split(',').every(cell => cell.trim() === '')) {
			emptyRowCount++;
			// If we have multiple empty rows in sequence, might indicate table separation
			if (emptyRowCount >= 2 && i > 0 && i < lines.length - 1) {
				// Check if there's a header-like row after empty rows
				const nextNonEmptyLine = lines.slice(i + 1).find(l => l.trim() !== '');
				if (nextNonEmptyLine) {
					const nextFirstCell = nextNonEmptyLine.split(',')[0]?.trim() || '';
					// If next line looks like a header (has text, not just numbers)
					if (nextFirstCell && isNaN(Number(nextFirstCell))) {
						dataRegionCount++;
					}
				}
			}
		} else {
			emptyRowCount = 0;
			// Check if this looks like a header row (first cell is text, not number)
			if (firstCell && isNaN(Number(firstCell)) && firstCell.length > 0) {
				// Check if previous row was empty or this is near the start
				if (i === 0 || (i > 0 && lines[i - 1].trim() === '')) {
					headerRowCount++;
				}
			}
		}
	}

	// Multiple tables detected if:
	// 1. Multiple "Table" headers found, OR
	// 2. Multiple header rows separated by empty rows, OR
	// 3. Multiple distinct data regions detected
	return tableHeaderCount > 1 || (headerRowCount > 1 && dataRegionCount > 0) || dataRegionCount > 1;
};

export const processDataFile = async (
	file: File,
	dispatch: any,
	onDataFileProcessed?: () => void,
	workbook?: any,
	sheetName?: string
): Promise<{ hasMultipleSheets: boolean; sheetNames?: string[]; workbook?: any } | void> => {
	console.log('🔄 [processDataFile] Starting file processing', {
		fileName: file.name,
		fileType: file.type,
		hasWorkbook: !!workbook,
		sheetName: sheetName || 'none',
	});

	try {
		dispatch({
			type: SHOW_LOADING_MODAL,
			data: { content: `Processing ${file.name}...` },
		});

		let csvText = '';

		if (file.name.endsWith('.csv') || file.type === 'text/csv') {
			console.log('📄 [processDataFile] Processing CSV file');
			csvText = await file.text();
			console.log('📄 [processDataFile] CSV text length:', csvText.length);
		} else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
			console.log('📊 [processDataFile] Processing Excel file');
			const XLSX = await import('xlsx');
			let currentWorkbook = workbook;
			
			// If workbook not provided, read it from file
			if (!currentWorkbook) {
				const arrayBuffer = await file.arrayBuffer();
				currentWorkbook = XLSX.read(new Uint8Array(arrayBuffer), {
					type: 'array',
					cellDates: true,
					cellNF: false,
					cellText: false,
					raw: false,
					codepage: 1252,
					dense: false,
					sheetStubs: false,
				});
			}

			if (!currentWorkbook.SheetNames || currentWorkbook.SheetNames.length === 0) {
				throw new Error('No sheets found in the Excel file');
			}

			// If multiple sheets and no sheet specified, return sheet info
			if (currentWorkbook.SheetNames.length > 1 && !sheetName) {
				console.log('📑 [processDataFile] Multiple sheets detected, returning sheet info', {
					sheetCount: currentWorkbook.SheetNames.length,
					sheetNames: currentWorkbook.SheetNames,
				});
				dispatch({ type: HIDE_LOADING_MODAL });
				return {
					hasMultipleSheets: true,
					sheetNames: currentWorkbook.SheetNames,
					workbook: currentWorkbook,
				};
			}

			console.log('📊 [processDataFile] Processing sheet:', sheetName || currentWorkbook.SheetNames[0]);

			// Use specified sheet or first sheet
			const targetSheetName = sheetName || currentWorkbook.SheetNames[0];
			const worksheet = currentWorkbook.Sheets[targetSheetName];

			if (!worksheet) {
				throw new Error(`Sheet "${targetSheetName}" not found`);
			}

			// Get the range of the worksheet to check for data
			const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
			if (range.e.r < 1) {
				throw new Error('The Excel sheet must contain at least a header row and one data row');
			}

			csvText = XLSX.utils.sheet_to_csv(worksheet, {
				forceQuotes: false,
				blankrows: false,
				strip: true,
				skipHidden: true,
				dateNF: 'yyyy-mm-dd',
			});
			console.log('📊 [processDataFile] Converted Excel to CSV, length:', csvText.length);
		}

		if (!csvText || csvText.trim() === '') {
			console.error('❌ [processDataFile] Empty CSV text');
			throw new Error(
				'The file appears to be empty or contains no readable data.'
			);
		}

		const lines = csvText.trim().split('\n');
		if (lines.length < 2) {
			throw new Error(
				'The file must contain at least a header row and one data row.'
			);
		}

		// Detect multiple tables and show warning if found
		const hasMultipleTables = detectMultipleTables(csvText);
		if (hasMultipleTables) {
			dispatch({
				type: SHOW_AUTH_MODAL,
				data: {
					content: 'This sheet contains multiple tables. Only the first table will be imported. Please ensure your sheet contains only one table for best results.',
					type: 'warning',
				},
			});
		}

		const regex = /Table/gi;
		const firstLine = lines[0];
		let processedCsvText = csvText;
		if (regex.test(firstLine)) {
			processedCsvText = lines.slice(1).join('\n');
			if (lines.length < 3) {
				throw new Error(
					'After removing table headers, the file must contain at least a header row and one data row.'
				);
			}
		}

		const delimiter = detectDelimiter(processedCsvText);
		const csvParse = Papa.parse(processedCsvText, {
			delimiter,
			dynamicTyping: true,
			skipEmptyLines: true,
			transformHeader: (header: string) =>
				header.toLowerCase().replace(/\W/g, '_'),
		});

		if (csvParse.errors && csvParse.errors.length > 0) {
			const criticalErrors = csvParse.errors.filter(
				(error) => error.type === 'Delimiter' || error.type === 'Quotes'
			);
			if (criticalErrors.length > 0) {
				throw new Error(
					`CSV parsing error: ${criticalErrors[0].message}`
				);
			}
		}

		if (
			!csvParse.data ||
			!Array.isArray(csvParse.data) ||
			csvParse.data.length === 0
		) {
			throw new Error('Could not detect headers in your file.');
		}

		const headerRow = csvParse.data[0];
		if (!Array.isArray(headerRow) || headerRow.length === 0) {
			throw new Error('Could not detect headers in your file.');
		}

		const fields: Array<{ title: string; type: string }> = [];
		headerRow.forEach((item: any, index: number) => {
			const cleanedItem = item?.toString().trim();
			if (cleanedItem && cleanedItem !== '') {
				const columnValues = csvParse.data
					.slice(1, Math.min(11, csvParse.data.length))
					.map((row: any) => (Array.isArray(row) ? row[index] : null))
					.filter((val) => val !== null && val !== undefined);
				const detectedType = detectDataType(columnValues);
				fields.push({ title: cleanedItem, type: detectedType });
			}
		});

		const dataLength = csvParse.data.length;
		const storedData = Papa.parse(processedCsvText, { header: false });
		storedData.data.shift();

		const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
		const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'CSV';
		const uploadDate = new Date().toISOString();

		// Store file metadata as a property on the storedFile array for backward compatibility
		// The array itself contains the data, but we attach metadata to it
		const fileDataWithMetadata = storedData.data;
		// Attach metadata to the array (non-enumerable to avoid breaking array operations)
		Object.defineProperty(fileDataWithMetadata, '_metadata', {
			value: {
				fileName: file.name,
				fileNameWithoutExt,
				fileSize: file.size,
				fileType: file.type,
				fileExtension,
				uploadDate,
				rowCount: storedData.data.length,
			},
			enumerable: false,
			writable: false,
		});

		dispatch({
			type: CHANGE_DATA,
			payload: {
				dataLength,
				schemaContent: fields,
				storedFile: fileDataWithMetadata,
				isUpload: true,
				schemaTitle: fileNameWithoutExt,
				dataTags: [],
				description: '',
			},
		});

		try {
			const tagsResponse = await fetch(
				`${process.env.EXPO_PUBLIC_AI_API}/generate-tags`,
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ data: fields }),
				}
			);
			const tagsResult = await tagsResponse.json();
			dispatch({
				type: CHANGE_DATA_TAGS,
				payload: JSON.parse(tagsResult),
			});
		} catch (err) {
			console.error('Error getting AI tags:', err);
		}

		dispatch({ type: SET_UPLOADER_STAGE, payload: 1 });
		dispatch({ type: HIDE_LOADING_MODAL });

		if (onDataFileProcessed) {
			onDataFileProcessed();
		} else if (navigationRef.isReady()) {
			(navigationRef as any).navigate('DataHandler');
		} else {
			Alert.alert(
				'Success',
				'File processed successfully! Please navigate to Data Handler to view your dataset.'
			);
		}
	} catch (error: any) {
		console.error('Error processing data file:', error);
		dispatch({ type: HIDE_LOADING_MODAL });
		dispatch({
			type: SHOW_AUTH_MODAL,
			data: {
				content: error.message || 'Error processing file',
				type: 'error',
			},
		});
	}
};

export const processMediaFile = async (
	file: File,
	dispatch: any,
	setPendingMediaFile: any,
	setMetadataDialogVisible: any,
	setPendingImageAnalysis?: any // Optional callback for Easy Mode image analysis
) => {
	console.log('🔄 [processMediaFile] Starting media file processing', {
		fileName: file.name,
		fileType: file.type,
		fileSize: file.size,
		sizeMB: (file.size / 1024 / 1024).toFixed(2),
	});

	try {
		dispatch({
			type: SHOW_LOADING_MODAL,
			data: { content: `Processing ${file.name}...` },
		});

		// Wrap FileReader in a Promise to properly await completion
		const base64Data = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			
			reader.onload = () => {
				console.log('📖 [processMediaFile] FileReader onload triggered');
				const result = reader.result as string;
				resolve(result);
			};

			reader.onerror = (error) => {
				console.error('❌ [processMediaFile] FileReader error:', error);
				reject(new Error('Failed to read file'));
			};

			reader.readAsDataURL(file);
		});

		const fileData = base64Data.split(',')[1] || base64Data;
		const fileType = getFileType(file.name, file.type);
		console.log('🔍 [processMediaFile] Detected file type:', fileType);

		const mediaFileData: any = {
			name: file.name,
			type: file.type,
			size: file.size,
			data: base64Data,
			fileData,
		};

		// For Easy Mode: Don't analyze image here - backend will handle it when message is sent
		// Just store the image data for later use

		// For images: Store image data and show it beside input (Easy Mode only)
		// Backend will analyze the image when user sends a message
		if (fileType === 'image') {
			console.log('🖼️ [processMediaFile] Image stored for Easy Mode (backend will analyze on send)');
			dispatch({ type: HIDE_LOADING_MODAL });
			
			// If callback provided (Easy Mode), store the image data
			// Backend will handle analysis when message is sent
			if (setPendingImageAnalysis) {
				console.log('✅ [processMediaFile] Setting pending image analysis with data URL');
				setPendingImageAnalysis({
					imageFile: file,
					imageData: base64Data, // This is already a data URL (data:image/...;base64,...)
					analysis: null, // Backend will analyze
				});
				console.log('✅ [processMediaFile] Image stored for Easy Mode, state updated');
			} else {
				// Fallback: upload directly if no callback (other screens)
				console.log('📤 [processMediaFile] No callback provided, uploading image directly');
				await uploadMediaFile(mediaFileData, dispatch);
			}
		} else if (fileType === 'video') {
			// For videos, still show metadata dialog if needed
			setPendingMediaFile(mediaFileData);
			console.log('💾 [processMediaFile] Set pending media file, opening metadata dialog for video');
			setMetadataDialogVisible(true);
			dispatch({ type: HIDE_LOADING_MODAL });
		} else {
			console.log('📤 [processMediaFile] Uploading non-image/video media file directly');
			await uploadMediaFile(mediaFileData, dispatch);
		}
	} catch (error: any) {
		console.error('❌ [processMediaFile] Error processing media file:', error);
		dispatch({ type: HIDE_LOADING_MODAL });
		Alert.alert('Error', error.message || 'Failed to process media file');
	}
};

export const uploadMediaFile = async (
	file: any,
	dispatch: any,
	metadata?: any
) => {
	try {
		dispatch({
			type: SHOW_LOADING_MODAL,
			data: { content: `Uploading ${file.name}...` },
		});

		// Build metadata object - only include social media fields if explicitly provided
		const mediaMetadata: any = {
			uploadedVia: 'fileDrop',
		};

		// Include analysis result if available (for images)
		if (metadata?.analysis) {
			mediaMetadata.analysis = metadata.analysis;
		}

		// Include description from analysis or user input
		if (metadata?.description) {
			mediaMetadata.description = metadata.description;
		}

		// Only include social media fields if they're explicitly provided (for videos or manual uploads)
		if (metadata?.location !== undefined) {
			mediaMetadata.location = metadata.location;
		}
		if (metadata?.scheduledDate !== undefined) {
			mediaMetadata.scheduledDate = metadata.scheduledDate;
		}
		if (metadata?.tags !== undefined) {
			mediaMetadata.tags = typeof metadata.tags === 'string'
				? metadata.tags.split(',').map((t: string) => t.trim())
				: metadata.tags;
		}

		// Include any other metadata fields
		Object.keys(metadata || {}).forEach(key => {
			if (!['analysis', 'description', 'location', 'scheduledDate', 'tags'].includes(key)) {
				mediaMetadata[key] = metadata[key];
			}
		});

		const mediaPayload = {
			name: file.name,
			data: file.fileData || file.data,
			type: file.type,
			metadata: mediaMetadata,
		};

		const result = await uploadGlobalAsset(mediaPayload);

		if (result && Array.isArray(result) && result.length > 0) {
			dispatch({ type: HIDE_LOADING_MODAL });
			
			// Show success message with analysis info if available
			const analysisInfo = metadata?.analysis?.response || 
			                     metadata?.analysis?.description || 
			                     metadata?.analysis?.analysis ||
			                     (typeof metadata?.analysis === 'string' ? metadata.analysis : null);
			const successMessage = analysisInfo
				? `${file.name} has been uploaded and analyzed!\n\nAnalysis: ${typeof analysisInfo === 'string' ? analysisInfo.substring(0, 200) + (analysisInfo.length > 200 ? '...' : '') : 'Completed'}`
				: `${file.name} has been uploaded to your media library!`;
			
			Alert.alert('Success', successMessage);

			// Only show scheduling alert if scheduledDate is explicitly set (for videos)
			if (metadata?.scheduledDate) {
				Alert.alert(
					'Scheduled',
					`Your media has been scheduled for posting on ${metadata.scheduledDate}. AI will handle the social media post creation.`
				);
			}
		} else {
			throw new Error('Invalid response from server');
		}
	} catch (error: any) {
		console.error('Error uploading media:', error);
		dispatch({ type: HIDE_LOADING_MODAL });
		Alert.alert(
			'Upload Failed',
			error.message || 'Failed to upload media file'
		);
	}
};

export const processDocumentFile = async (
	file: File,
	dispatch: any,
	folderId?: string | null
) => {
	console.log('📄 [processDocumentFile] Starting document processing', {
		fileName: file.name,
		fileType: file.type,
		fileSize: file.size,
	});

	try {
		dispatch({
			type: SHOW_LOADING_MODAL,
			data: { content: `Uploading ${file.name}...` },
		});

		// Read file as base64
		const reader = new FileReader();
		const base64Data = await new Promise<string>((resolve, reject) => {
			reader.onloadend = () => {
				const result = reader.result as string;
				resolve(result);
			};
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});

		// Determine MIME type - use file.type or infer from extension
		let mimeType = file.type;
		if (!mimeType || mimeType === 'application/octet-stream') {
			const extension = file.name.split('.').pop()?.toLowerCase();
			const mimeTypeMap: Record<string, string> = {
				'pdf': 'application/pdf',
				'doc': 'application/msword',
				'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
				'xls': 'application/vnd.ms-excel',
				'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				'ppt': 'application/vnd.ms-powerpoint',
				'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
				'txt': 'text/plain',
				'rtf': 'application/rtf',
				'csv': 'text/csv',
				'odt': 'application/vnd.oasis.opendocument.text',
				'ods': 'application/vnd.oasis.opendocument.spreadsheet',
				'odp': 'application/vnd.oasis.opendocument.presentation',
			};
			mimeType = extension ? (mimeTypeMap[extension] || 'application/octet-stream') : 'application/octet-stream';
		}

		// Import apis dynamically to avoid circular dependencies
		const { uploadUserDocument } = await import('@api/api');

		await uploadUserDocument({
			name: file.name,
			type: mimeType,
			size: file.size,
			fileData: base64Data,
			folderId: folderId || null,
		});

		dispatch({ type: HIDE_LOADING_MODAL });
		dispatch({
			type: SHOW_AUTH_MODAL,
			data: { content: `${file.name} has been uploaded to your documents!`, type: 'success' }
		});

		console.log('✅ [processDocumentFile] Document uploaded successfully');
	} catch (error: any) {
		console.error('❌ [processDocumentFile] Error uploading document:', error);
		dispatch({ type: HIDE_LOADING_MODAL });
		dispatch({
			type: SHOW_AUTH_MODAL,
			data: {
				content: error.message || 'Failed to upload document',
				type: 'error',
			},
		});
	}
};

export const handleDroppedFile = async (
	file: File,
	dispatch: any,
	setPendingMediaFile: any,
	setMetadataDialogVisible: any,
	onDataFileProcessed?: () => void,
	folderId?: string | null
) => {
	console.log('🎯 [handleDroppedFile] Called', {
		fileName: file.name,
		fileType: file.type,
		fileSize: file.size,
	});
	const fileType = getFileType(file.name, file.type);
	console.log('🔍 [handleDroppedFile] Detected file type:', fileType);

	switch (fileType) {
		case 'csv':
		case 'excel':
			await processDataFile(file, dispatch, onDataFileProcessed);
			break;
		case 'image':
		case 'video':
			await processMediaFile(
				file,
				dispatch,
				setPendingMediaFile,
				setMetadataDialogVisible
			);
			break;
		case 'document':
			await processDocumentFile(file, dispatch, folderId);
			break;
		default:
			Alert.alert(
				'Unsupported File',
				`File type "${fileType}" is not supported. Please drop CSV, Excel, image, video, or document files (PDF, Word, PowerPoint, etc.).`
			);
	}
};

export const handleDrop = async (
	e: any,
	fileDrop: boolean,
	dispatch: any,
	setPendingMediaFile: any,
	setMetadataDialogVisible: any,
	onDataFileProcessed?: () => void,
	folderId?: string | null
) => {
	if (!fileDrop || Platform.OS !== 'web') return;

	console.log('🎯 File drop detected', {
		files: e.dataTransfer?.files?.length,
		fileDrop,
		platform: Platform.OS,
	});

	e.preventDefault();
	e.stopPropagation();

	const files = Array.from(e.dataTransfer?.files || []);
	console.log('🎯 Processing files:', files.length);

	if (files.length === 0) {
		console.warn('⚠️ No files in drop event');
		return;
	}

	for (const file of files) {
		await handleDroppedFile(
			file as File,
			dispatch,
			setPendingMediaFile,
			setMetadataDialogVisible,
			onDataFileProcessed,
			folderId
		);
	}
};

export const handleMetadataSubmit = async (
	pendingMediaFile: any,
	mediaMetadata: any,
	dispatch: any,
	setMetadataDialogVisible: any,
	setPendingMediaFile: any,
	setMediaMetadata: any
) => {
	if (!pendingMediaFile) return;

	await uploadMediaFile(pendingMediaFile, dispatch, mediaMetadata);
	setMetadataDialogVisible(false);
	setPendingMediaFile(null);
	setMediaMetadata({
		description: '',
		location: '',
		scheduledDate: '',
		tags: '',
	});
};
